import { FileText, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cur } from '@/lib/currency';

function fmt(n) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const PAY_STATE = {
  paid:                   { label: 'مدفوع',      cls: 'text-emerald-600 bg-emerald-50', icon: CheckCircle2 },
  partial:                { label: 'جزئي',        cls: 'text-amber-600 bg-amber-50',    icon: Clock },
  in_payment:             { label: 'قيد الدفع',   cls: 'text-blue-600 bg-blue-50',      icon: Clock },
  not_paid:               { label: 'غير مدفوع',  cls: 'text-red-600 bg-red-50',        icon: AlertTriangle },
  reversed:               { label: 'مرجعة',       cls: 'text-gray-600 bg-gray-50',      icon: FileText },
};

export default function PaymentsTab({ order, orderId }) {
  const navigate = useNavigate();
  const invoices = order.invoices || [];

  const totalDue = invoices.reduce((s, i) => s + (i.amount_residual || 0), 0);
  const totalPaid = (order.amount_total || 0) - totalDue;

  if (invoices.length === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">لا توجد فواتير مرتبطة بهذا الطلب</p>
          {order.state === 'sale' && (
            <p className="text-xs text-muted-foreground/60 mt-1">يمكنك إنشاء فاتورة من Odoo</p>
          )}
        </div>
        {order.state === 'sale' && (
          <Button onClick={() => navigate(`/collections/new?order_id=${orderId}&customer_id=${Array.isArray(order.partner_id) ? order.partner_id[0] : ''}&customer_name=${encodeURIComponent(Array.isArray(order.partner_id) ? order.partner_id[1] : '')}`)}
            className="w-full bg-primary rounded-xl h-11 gap-2">
            + تسجيل تحصيل
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-center">
          <p className="text-2xl font-black text-emerald-600">{fmt(totalPaid)}</p>
          <p className="text-xs text-emerald-700 mt-0.5">إجمالي المدفوع</p>
        </div>
        <div className={`border rounded-2xl p-4 text-center ${totalDue > 0 ? 'bg-amber-50 border-amber-100' : 'bg-muted border-border'}`}>
          <p className={`text-2xl font-black ${totalDue > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>{fmt(totalDue)}</p>
          <p className={`text-xs mt-0.5 ${totalDue > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>المتبقي</p>
        </div>
      </div>

      {/* Progress */}
      {order.amount_total > 0 && (
        <div className="space-y-1">
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full"
              style={{ width: `${Math.min(100, (totalPaid / order.amount_total) * 100)}%` }} />
          </div>
          <p className="text-xs text-muted-foreground text-left">
            {Math.round((totalPaid / order.amount_total) * 100)}% مدفوع
          </p>
        </div>
      )}

      {/* Invoices */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">الفواتير</p>
        {invoices.map(inv => {
          const ps = PAY_STATE[inv.payment_state] || PAY_STATE.not_paid;
          const Icon = ps.icon;
          return (
            <div key={inv.id} className="bg-card border border-border rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <p className="font-semibold text-foreground text-sm">{inv.name}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ${ps.cls}`}>
                  <Icon className="w-3 h-3" /> {ps.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-muted-foreground">المبلغ</p>
                  <p className="font-semibold text-foreground">{fmt(inv.amount_total)} {cur()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">المتبقي</p>
                  <p className={`font-semibold ${inv.amount_residual > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {fmt(inv.amount_residual)} {cur()}
                  </p>
                </div>
                {inv.invoice_date && (
                  <div>
                    <p className="text-muted-foreground">تاريخ الفاتورة</p>
                    <p className="font-medium text-foreground">{new Date(inv.invoice_date).toLocaleDateString('ar-EG')}</p>
                  </div>
                )}
                {inv.invoice_date_due && (
                  <div>
                    <p className="text-muted-foreground">تاريخ الاستحقاق</p>
                    <p className={`font-medium ${inv.invoice_date_due < new Date().toISOString().slice(0,10) && inv.amount_residual > 0 ? 'text-red-600' : 'text-foreground'}`}>
                      {new Date(inv.invoice_date_due).toLocaleDateString('ar-EG')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Collection button */}
      {totalDue > 0 && (
        <Button onClick={() => navigate(`/collections/new?customer_id=${Array.isArray(order.partner_id) ? order.partner_id[0] : ''}&customer_name=${encodeURIComponent(Array.isArray(order.partner_id) ? order.partner_id[1] : '')}`)}
          className="w-full bg-primary rounded-xl h-11 gap-2">
          + تسجيل تحصيل
        </Button>
      )}
    </div>
  );
}